import { nextTick, type App, type Plugin } from 'vue';
import type { Router, RouteLocationNormalized } from 'vue-router';
import { useAutopilotStore } from '@llmor/autopilot-vue';
import {
  beginRouteTransition,
  settleRouteTransition,
  abortRouteTransition,
  setRouteReadinessLogger,
  type RouteTransitionToken,
} from './routeReadiness';

/**
 * Vue plugin that wires the router into autopilot:
 *
 *  - mirrors the active route into `context.current_route` so the assistant can reason about
 *    where the user is;
 *  - locks every navigation behind a route readiness context resolution (see `routeReadiness.ts`),
 *    so a function response that triggered the navigation is withheld until the destination page
 *    has published its context (or the navigation settles unclaimed).
 *
 * Install after pinia and the router:
 *   app.use(AutopilotRouterPlugin, { router });
 */
export interface AutopilotRouterPluginOptions {
  router: Router;
}

// trim the route down to a serializable, assistant-friendly snapshot
function describeRoute(route: RouteLocationNormalized) {
  return {
    name: typeof route.name === 'string' ? route.name : undefined,
    path: route.path,
    full_path: route.fullPath,
    params: { ...route.params },
    query: { ...route.query },
  };
}

const AutopilotRouterPlugin: Plugin<AutopilotRouterPluginOptions> = {
  install(_app: App, { router }: AutopilotRouterPluginOptions) {
    const store = useAutopilotStore();

    // trace the readiness lifecycle into the panel's activity log for debugging
    // the default 20 entries evict too fast to trace a navigation (completion chatter alone fills them)
    store.activityLogCap = 250;
    setRouteReadinessLogger((message) => store.addActivity('info', message));

    // settle/abort must act on the lock of the navigation that triggered them, never on a newer
    // one: vue-router reports a cancelled navigation's failure only after the cancelling
    // navigation has acquired its own lock, and the settle below is deferred by two ticks. The
    // token is threaded from beforeEach to afterEach/onError via the navigation's `to` object,
    // which vue-router passes through the whole pipeline by identity.
    const tokens = new WeakMap<RouteLocationNormalized, RouteTransitionToken>();

    // every navigation (including the initial one) locks function responses until the destination
    // page is ready — the page unlocks by itself (claim + ready, or unclaimed auto-release below)
    router.beforeEach((to) => {
      tokens.set(
        to,
        beginRouteTransition((label) => store.client.beginContextResolution(label), `route:${to.fullPath}`)
      );
    });

    // afterEach also fires for the initial navigation, so the context is seeded on load
    router.afterEach(async (to, _from, failure) => {
      const token = tokens.get(to);
      if (failure) {
        // a failed navigation never became the current route — leave context.current_route alone
        if (token) abortRouteTransition(token);
        return;
      }
      store.context.current_route = describeRoute(to);
      // give the destination page time to render, mount and claim the transition before deciding
      // it does not participate: two ticks cover the route-update render and the mount flush
      await nextTick();
      await nextTick();
      if (token) settleRouteTransition(token);
    });

    // a navigation that errors never reaches afterEach — release its lock
    router.onError((_error, to) => {
      const token = tokens.get(to);
      if (token) abortRouteTransition(token);
    });
  },
};

export default AutopilotRouterPlugin;
